import { motion } from "framer-motion";
import { Mic, Sparkles } from "lucide-react";

/**
 * Voice Assistant section — Alexa & Google Home compatibility.
 * Sits directly below the Dynamic Series scrolling scenes.
 */

const COMMANDS = [
  "“Alexa, set Movie Night.”",
  "“Hey Google, dim the living room to thirty percent.”",
  "“Alexa, lock the front door.”",
  "“Hey Google, good night.”",
];

export default function VoiceAssistant() {
  return (
    <section
      data-testid="voice-assistant-section"
      className="relative bg-[var(--gx-black)] overflow-hidden"
    >
      {/* Ambient gold gradient backdrop */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(201,169,97,0.16),transparent_70%)] blur-3xl" />
        <div className="absolute -bottom-40 -right-32 w-[620px] h-[620px] rounded-full bg-[radial-gradient(circle,rgba(201,169,97,0.10),transparent_70%)] blur-3xl" />
      </div>
      <div className="absolute inset-0 grain pointer-events-none opacity-60" />

      <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-20 items-center">
          {/* Copy */}
          <div>
            <p className="eyebrow mb-5">Voice Control</p>
            <h2
              className="h1-display text-white max-w-[16ch]"
              data-testid="voice-assistant-heading"
            >
              Speak to your home.
              <br />
              <span className="text-dim">It already knows.</span>
            </h2>
            <p className="lead mt-6 max-w-[52ch]">
              GenieX is natively compatible with <span className="text-white">Amazon Alexa</span> and{" "}
              <span className="text-white">Google Home</span>. Trigger scenes, adjust lights,
              lock doors, set the climate — all hands-free, in the language you already speak.
            </p>

            {/* Logos / badges */}
            <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="voice-badges">
              <Badge label="Works with Alexa" Icon={Mic} />
              <Badge label="Hey Google" Icon={Sparkles} />
            </div>

            {/* Example commands */}
            <ul className="mt-10 space-y-3 max-w-[460px]" data-testid="voice-commands-list">
              {COMMANDS.map((cmd, i) => (
                <motion.li
                  key={cmd}
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ delay: i * 0.08, duration: 0.5, ease: "easeOut" }}
                  className="flex items-start gap-3 text-white/85"
                >
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gold shrink-0" />
                  <span className="font-display text-[18px] lg:text-[20px] tracking-[-0.01em]">
                    {cmd}
                  </span>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Visual */}
          <div className="relative">
            <div className="relative aspect-square max-w-[520px] mx-auto">
              {/* Outer pulse rings */}
              <PulseRing delay={0} />
              <PulseRing delay={1.2} />
              <PulseRing delay={2.4} />

              {/* Center orb */}
              <div className="absolute inset-[18%] rounded-full bg-[radial-gradient(circle_at_30%_30%,rgba(255,236,196,0.95),rgba(201,169,97,0.65)_42%,rgba(40,30,12,0.95)_78%)] shadow-[0_30px_120px_rgba(201,169,97,0.45)]">
                <div className="absolute inset-0 rounded-full border border-white/10" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Mic className="w-14 h-14 lg:w-16 lg:h-16 text-[var(--gx-bg)]" strokeWidth={1.5} />
                </div>
              </div>

              {/* Floating chip — Alexa */}
              <FloatingChip
                className="left-[-6%] top-[12%]"
                label="Alexa"
                hint="Set Movie Night"
                testid="chip-alexa"
              />
              {/* Floating chip — Google */}
              <FloatingChip
                className="right-[-4%] bottom-[14%]"
                label="Google Home"
                hint="Dim to 30%"
                testid="chip-google"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Badge({ label, Icon }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] backdrop-blur px-4 py-2 text-[12px] tracking-[0.18em] uppercase text-white/90">
      <Icon className="w-3.5 h-3.5 text-gold" />
      {label}
    </span>
  );
}

function PulseRing({ delay }) {
  return (
    <motion.span
      initial={{ scale: 0.6, opacity: 0.55 }}
      animate={{ scale: 1.15, opacity: 0 }}
      transition={{ duration: 3.6, repeat: Infinity, ease: "easeOut", delay }}
      className="absolute inset-0 rounded-full border border-[rgba(201,169,97,0.45)]"
    />
  );
}

function FloatingChip({ className, label, hint, testid }) {
  return (
    <motion.div
      initial={{ y: 12, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      data-testid={testid}
      className={`absolute ${className} rounded-2xl border border-white/15 bg-[rgba(14,14,18,0.78)] backdrop-blur-xl px-4 py-3 shadow-[0_20px_60px_rgba(0,0,0,0.55)]`}
    >
      <p className="text-[10px] tracking-[0.22em] uppercase text-gold">{label}</p>
      <p className="text-white text-sm mt-1 whitespace-nowrap">{hint}</p>
    </motion.div>
  );
}
